import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Trademark Credentials | Legal Terms & Conditions</title>
        <meta name="description" content="Trademark Credentials Terms of Service. Read our terms and conditions for using our trademark registration services. Legal guidelines and user responsibilities." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        <p className="text-gray-600 mb-12">Last updated: December 2025</p>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing and using Trademark Credentials's website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
            <p>
              Trademark Credentials provides trademark registration services, copyright registration, comprehensive trademark searches, company formation assistance, and Amazon Brand Registry services. We act as filing agents and provide legal support for intellectual property protection.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
            <p className="mb-3">When using our services, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not use our services for any fraudulent or illegal purposes</li>
              <li>Not infringe upon the intellectual property rights of others</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Fees and Payment</h2>
            <p className="mb-3">
              Our services require payment of service fees in addition to government filing fees. Payment terms include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Service fees are due at the time of order placement</li>
              <li>Government filing fees are separate and paid directly to the relevant authorities</li>
              <li>Prices are subject to change with notice</li>
              <li>Refunds are handled according to our refund policy</li>
              <li>You are responsible for all applicable taxes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
            <p>
              All content on our website, including text, graphics, logos, and software, is the property of Trademark Credentials or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Service Limitations and Disclaimers</h2>
            <p className="mb-3">
              While we strive to provide excellent service, please note:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We cannot guarantee trademark approval by the USPTO or other government agencies</li>
              <li>Processing times are estimates and may vary based on government agency workload</li>
              <li>Our services do not constitute legal advice; consult an attorney for specific legal matters</li>
              <li>We are not responsible for rejection due to inaccurate information provided by clients</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Trademark Credentials shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our services.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Trademark Credentials and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your violation of these Terms or your use of our services.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Confidentiality</h2>
            <p>
              We maintain strict confidentiality of all client information and case details. We will not disclose your information except as required by law or as necessary to provide our services.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your access to our services at any time, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising from these Terms or our services shall be resolved through binding arbitration in accordance with applicable arbitration rules.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms on our website. Your continued use of our services after such changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="mb-3">For questions about these Terms of Service, please contact us:</p>
            <ul className="list-none space-y-2 ml-4">
              <li>Email: info@trademarkcredentials.com</li>
              <li>Phone: +1 (877) 410-0136</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsOfService;
